import React, { useState, useEffect } from "react";
import {
  Grid,
  Box,
  TableCell,
  TableRow,
  TableBody,
  Typography,
  Paper,
  Select,
  MenuItem,
} from "@mui/material";
import { Search } from "@mui/icons-material";
import { makeStyles } from "@mui/styles";
import Controls from "../../../components/controls/Controls";
import useTable from "../../../components/customHelpers/useTable";
import Notify from "../../../components/customHelpers/Notify";
import Notification from "../../../components/customHelpers/Notification";
import ConfirmDialog from "../../../components/customHelpers/ConfirmDialog";
import Norecords from "../../../components/customHelpers/Norecords";
import Loading from "../../../components/customHelpers/Loading";
import Layout from "../../../components/layout/Layout";
import Admin from "../../../components/auth/Admin";
import Role from "../../../actions/role";
import produce from "immer";
import { useHistory } from "react-router-dom";
import { IoMdAdd } from "react-icons/io";
import { FiEdit, FiEye } from "react-icons/fi";
import { MdOutlineDelete } from "react-icons/md";
import { AiOutlineSearch } from "react-icons/ai";
import InfoComponent from "../../../components/InfoComponent";

const useStyles = makeStyles((theme) => ({
  image: {
    borderRadius: "55%",
    height: "45px",
    width: "45px",
  },
}));

const headCells = [
  { id: "name", label: "Name" },
  { id: "description", label: "Description" },
  { id: "permissions", label: "Permissions" },
  { id: "actions", label: "Actions", disableSorting: true },
];

const RolePage = () => {
  const classes = useStyles();
  const { NotifyMessage, notify, setNotify } = Notify();
  const { viewAllRoles, deleteRole } = Role();
  const [recordForEdit, setRecordForEdit] = useState(null);
  const [openPopup, setOpenPopup] = useState(false);
  const [edite, setEdit] = useState(false);
  const [roles, setRoles] = useState([]);
  const [values, setValues] = useState({
    loading: false,
    reload: false,
  });
  const { loading, reload } = values;
  const [confirmDialog, setConfirmDialog] = useState({
    isOpen: false,
    title: "",
    subTitle: "",
  });
  const [filterFn, setFilterFn] = useState({
    fn: (items) => {
      return items;
    },
  });
  const { TblContainer, TblHead, TblPagination, recordsAfterPagingAndSorting } =
    useTable(roles, headCells, filterFn);
  const history = useHistory();

  useEffect(() => {
    getAllRoles();
  }, [reload]);

  const getAllRoles = () => {
    setValues({ ...values, loading: true });
    viewAllRoles().then((data) => {
      if (data.err) {
        NotifyMessage({
          message: data.err,
          type: "error",
        });
      } else if (data.roles) {
        setValues({
          ...values,
          loading: false,
        });
        setRoles(data.roles);
      }
    });
  };

  const onDelete = (Id) => {
    setConfirmDialog({
      ...confirmDialog,
      isOpen: false,
    });
    deleteRole(Id).then((data) => {
      if (data.err) {
        NotifyMessage({
          message: data.err,
          type: "error",
        });
      } else {
        NotifyMessage({
          message: data.message,
          type: "success",
        });
        setValues({ ...values, reload: !reload });
        setRoles(
          produce(roles, (draft) => {
            const index = roles.findIndex((role) => role.id === Id);
            if (index !== -1) draft.splice(index, 1);
          })
        );
      }
    });
  };

  const handleSearch = (e) => {
    e.target.value = e.target.value.trimLeft();
    let target = e.target;
    setFilterFn({
      fn: (items) => {
        const columns = ["name", "description"];

        if (target.value == "") return items;
        else {
          return items?.filter((x) => {
            return columns.some((column) => {
              return (
                x[column]
                  ?.toString()
                  .toLowerCase()
                  .indexOf(target.value.toLowerCase()) > -1
              );
            });
          });
        }
      },
    });
  };

  return (
    <Layout>
      <Admin>
        <div className="card">
          <Box
            sx={{
              display: "flex",
              alignItems: "center",
              justifyContent: "space-between",
              flexWrap: { xs: "wrap", sm: "nowrap" },
              gap: { xs: 1, sm: 0 },
              "& > .newButton": {
                marginLeft: { xs: "auto", sm: "0" },
              },
              "@media (max-width: 455px)": {
                flexDirection: "column",
                alignItems: "flex-start",
              },
            }}
          >
            <Typography
              variant="h6"
              className="page-header"
              sx={{ whiteSpace: "nowrap" }}
            >
              Roles
            </Typography>
            <Controls.Button
              text="Add Role"
              variant="outlined"
              startIcon={<IoMdAdd />}
              className="newButton"
              onClick={() => {
                history.push(`/addRol`);
              }}
            />
          </Box>
          <InfoComponent pageKey="roles" />

          <div
            className="search-header-container"
            style={{ marginBottom: 0, flex: 1, maxWidth: 260, minWidth: 260 }}
          >
            <div className="search-header-flex">
              <div className="search-container">
                <AiOutlineSearch className="search_icon" />
                <input
                  type="text"
                  onChange={handleSearch}
                  placeholder="Search Roles"
                  className="search-input"
                  style={{ width: "100%" }}
                />
              </div>
            </div>
          </div>

          {loading ? (
            <Loading />
          ) : (
            <Paper style={{ overflowX: "auto" }}>
              <TblContainer>
                <TblHead />
                <TableBody>
                  {recordsAfterPagingAndSorting().length > 0 ? (
                    recordsAfterPagingAndSorting().map((item) => (
                      <TableRow key={item.Id}>
                        <TableCell>{item.name}</TableCell>
                        <TableCell>{item.description}</TableCell>
                        <TableCell>{item.rolePermissions?.length}</TableCell>
                        <TableCell>
                          <Controls.ActionButton
                            color="primary"
                            title="Update"
                            onClick={() => {
                              history.push(`/updateRole/${item.id}`);
                            }}
                          >
                            <FiEdit fontSize="medium" />
                          </Controls.ActionButton>
                          <Controls.ActionButton
                            color="secondary"
                            title="Delete"
                            onClick={() => {
                              setConfirmDialog({
                                isOpen: true,
                                title: "Are you sure to delete this Role?",
                                subTitle: "You can't undo this operation",
                                onConfirm: () => {
                                  onDelete(item.id);
                                },
                              });
                            }}
                          >
                            <MdOutlineDelete fontSize="medium" />
                          </Controls.ActionButton>
                          <Controls.ActionButton
                            color="primary"
                            title="View Detail"
                            onClick={() => {
                              history.push(`/role/${item.id}`);
                            }}
                          >
                            <FiEye fontSize="medium" />
                          </Controls.ActionButton>
                        </TableCell>
                      </TableRow>
                    ))
                  ) : (
                    <Norecords col={6} />
                  )}
                </TableBody>
              </TblContainer>
              <TblPagination />
            </Paper>
          )}

          <ConfirmDialog
            confirmDialog={confirmDialog}
            setConfirmDialog={setConfirmDialog}
          />
          <Notification notify={notify} setNotify={setNotify} />
        </div>
      </Admin>
    </Layout>
  );
};

export default RolePage;
